import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import CityAutocomplete from "./CityAutocomplete";
import { useAuth } from "@/lib/auth";
import { OwnerClientSearch, OwnerClientSummary } from "./OwnerClientPicker";

const EMPTY_OWNER = {
  owner_type: "privato", owner_first_name: "", owner_last_name: "", owner_phone: "", owner_email: "",
  owner_vat_number: "", owner_company_name: "", owner_company_fiscal_code: "", owner_company_address: "",
};

/**
 * Iter209dz3 — Form unificato per creare/modificare un immobile.
 *
 * Modalita':
 *  - `wizard=false` (default): single-step. Usato da Marketplace e Modifica.
 *  - `wizard=true`: due fasi distinte con Avanti/Indietro. Usato in /properties.
 *      • Fase 1: Indirizzo + Citta + Prov + CAP + Note (+ extraFields).
 *      • Fase 2: Tipo cliente (Privato / P.IVA) + relativi campi.
 *        Per P.IVA: all'inizio mostra SOLO il campo P.IVA. Dopo il lookup
 *        VIES (success o fail) mostra tutti gli altri campi — prepopolati
 *        se trovati, vuoti ed editabili se non trovati. Link "Inserisci
 *        manualmente" per rivelarli senza lookup.
 */
export default function NewPropertyForm({
  value,
  onChange,
  onSubmit,
  submitting = false,
  submitLabel = "Crea immobile",
  testidPrefix = "new-prop",
  showNotes = true,
  onCancel,
  cancelLabel = "← Indietro",
  extraFields = null,
  wizard = false,
  onStepChange,
  hideOwnerFields = false,
  clientPicker = false,
  clientsProId = null,
}) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [ownerMode, setOwnerMode] = useState(null);
  const [pickedClient, setPickedClient] = useState(null);
  const [vatLoading, setVatLoading] = useState(false);
  const [vatError, setVatError] = useState("");
  const [vatDone, setVatDone] = useState(false);
  // Iter209dz3 — Progressive disclosure dei campi azienda in Fase 2.
  const [aziendaRevealed, setAziendaRevealed] = useState(false);
  // Iter209dz8 — "+ Dettagli" per nascondere Telefono e Email di default.
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const vatTimerRef = useRef(null);
  const vatLastRef = useRef("");

  const tid = (suffix) => `${testidPrefix}-${suffix}`;

  // Se il form arriva gia' popolato (edit) → rivela subito i campi azienda
  // e i contatti (altrimenti sembrerebbe che non ci siano).
  useEffect(() => {
    if (!aziendaRevealed && value.owner_type === "azienda" &&
        ((value.owner_company_name || "").trim() ||
         (value.owner_company_address || "").trim())) {
      setAziendaRevealed(true);
    }
  }, [value.owner_type, value.owner_company_name, value.owner_company_address, aziendaRevealed]);

  useEffect(() => {
    if (!contactsExpanded &&
        ((value.owner_phone || "").trim() || (value.owner_email || "").trim())) {
      setContactsExpanded(true);
    }
  }, [value.owner_phone, value.owner_email, contactsExpanded]);

  useEffect(() => {
    if (typeof onStepChange === "function") onStepChange(step);
  }, [step, onStepChange]);

  const runVatLookup = async (vatRaw) => {
    const vat = (vatRaw ?? value.owner_vat_number ?? "").replace(/\s/g, "").trim();
    if (!vat) { setVatError("Inserisci la P.IVA prima di cercare"); return; }
    setVatLoading(true); setVatError(""); setVatDone(false);
    try {
      const { data } = await api.get(`/vat/lookup`, { params: { vat } });
      if (!data?.found || !data?.company) {
        setVatError("P.IVA non trovata — inserisci i dati manualmente");
        setAziendaRevealed(true);
        return;
      }
      const c = data.company;
      onChange({
        owner_company_name: c.name || value.owner_company_name,
        owner_company_address: c.address || "",
      });
      setVatDone(true);
      setAziendaRevealed(true);
      toast.success(`Dati aziendali caricati: ${c.name}`);
    } catch (_e) {
      setVatError("P.IVA non trovata — inserisci i dati manualmente");
      setAziendaRevealed(true);
    } finally { setVatLoading(false); }
  };

  // Auto-lookup VIES al raggiungimento di 11 cifre (debounce 600ms).
  useEffect(() => {
    const raw = (value.owner_vat_number || "").replace(/\D/g, "");
    if (vatTimerRef.current) clearTimeout(vatTimerRef.current);
    if (value.owner_type !== "azienda") return;
    if (raw.length !== 11) { setVatDone(false); return; }
    if (raw === vatLastRef.current) return;
    vatTimerRef.current = setTimeout(() => {
      vatLastRef.current = raw;
      runVatLookup(raw);
    }, 600);
    return () => { if (vatTimerRef.current) clearTimeout(vatTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.owner_vat_number, value.owner_type]);

  const validatePropertyFields = () => {
    if (!value.address?.trim() || !value.city?.trim()) {
      toast.error("Indirizzo e Città sono obbligatori");
      return false;
    }
    if (!value.province) {
      toast.error("Seleziona la città dall'elenco per compilare provincia e CAP");
      return false;
    }
    return true;
  };

  const validateOwnerFields = () => {
    if (!value.owner_type) {
      toast.error('Seleziona il tipo cliente (Privato o P.IVA)');
      return false;
    }
    if (value.owner_type === "privato") {
      if (!value.owner_first_name?.trim() || !value.owner_last_name?.trim()) {
        toast.error("Nome e Cognome del proprietario sono obbligatori");
        return false;
      }
    } else if (value.owner_type === "azienda") {
      if (!value.owner_vat_number?.trim()) {
        toast.error("P.IVA obbligatoria per clienti P.IVA");
        return false;
      }
      if (!value.owner_company_name?.trim()) {
        toast.error("Ragione sociale obbligatoria per clienti P.IVA");
        return false;
      }
    }
    return true;
  };

  const goNext = () => { if (validatePropertyFields()) setStep(2); };
  const goBack = () => setStep(1);

  const handleSubmit = () => {
    if (!validatePropertyFields()) { if (wizard) setStep(1); return; }
    if (!hideOwnerFields && clientPicker) {
      if (!ownerMode) { toast.error("Scegli \"Cliente esistente\" o \"Nuovo cliente\""); return; }
      if (ownerMode === "existing") {
        if (!pickedClient) { toast.error("Seleziona un cliente"); return; }
        onSubmit(undefined, undefined, { fromExistingClient: true });
        return;
      }
    }
    if (!hideOwnerFields && !validateOwnerFields()) return;
    onSubmit();
  };

  const switchOwnerMode = (mode) => {
    if (mode === ownerMode) return;
    setOwnerMode(mode);
    setPickedClient(null);
    setAziendaRevealed(false);
    setContactsExpanded(false);
    vatLastRef.current = "";
    onChange({ ...EMPTY_OWNER });
  };

  const pickClient = (c) => {
    setPickedClient(c);
    onChange({
      ...EMPTY_OWNER,
      owner_type: c.owner_type === "azienda" ? "azienda" : "privato",
      owner_first_name: c.first_name || "",
      owner_last_name: c.last_name || "",
      owner_company_name: c.owner_company_name || "",
      owner_email: c.email || "",
      owner_phone: c.phone || "",
      owner_vat_number: c.owner_vat_number || "",
      owner_company_fiscal_code: c.owner_company_fiscal_code || "",
      owner_company_address: c.owner_company_address || "",
    });
  };

  const unpickClient = () => { setPickedClient(null); onChange({ ...EMPTY_OWNER }); };
  const submitBlocked = clientPicker && !hideOwnerFields && (!ownerMode || (ownerMode === "existing" && !pickedClient));

  // ===== Blocchi riutilizzabili =====
  const OwnerTypeSelector = (
    <div data-testid={tid("owner-type")}>
      <Label>Tipo cliente *</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        {[
          { v: "privato", label: "Privato" },
          { v: "azienda", label: "P.IVA" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange({ owner_type: opt.v })}
            className={
              "border-2 rounded-md py-2 text-sm font-medium transition-all " +
              (value.owner_type === opt.v
                ? "border-purple-500 bg-purple-50 text-purple-800"
                : "border-slate-200 bg-white text-slate-700 hover:border-purple-300")
            }
            data-testid={tid(`owner-type-${opt.v}`)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  const PropertyFieldsBlock = (
    <>
      <div>
        <Label>Indirizzo immobile *</Label>
        <Input
          value={value.address || ""}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="Via Roma 12"
          data-testid={tid("address")}
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <Label>Città immobile *{" "}
            <span className="text-xs text-muted-foreground font-normal">
              (inizia a scrivere per selezionare — provincia e CAP verranno compilati automaticamente)
            </span>
          </Label>
          <CityAutocomplete
            value={value.city || ""}
            onChange={(v) => onChange({ city: v, province: "", zip_code: "" })}
            onPick={(c) => onChange({ city: c.name, province: c.province, zip_code: c.cap })}
            placeholder="Milano"
            testid={tid("city")}
          />
        </div>
        <div>
          <Label>Prov. (auto)</Label>
          <Input
            value={value.province || ""}
            readOnly
            disabled
            className="bg-gray-50"
            placeholder="MI"
            maxLength={2}
            data-testid={tid("province")}
          />
        </div>
      </div>
      <div>
        <Label>CAP (auto)</Label>
        <Input
          value={value.zip_code || ""}
          readOnly
          disabled
          className="bg-gray-50"
          placeholder="20121"
          maxLength={5}
          data-testid={tid("cap")}
        />
      </div>
      {showNotes && (
        <div>
          <Label>Note</Label>
          <Textarea
            value={value.notes || ""}
            onChange={(e) => onChange({ notes: e.target.value })}
            rows={2}
            data-testid={tid("notes")}
          />
        </div>
      )}
      {extraFields}
    </>
  );

  // Iter209dz8 — Toggle "+ Dettagli" per Telefono + Email (opzionali).
  // Reso come costante JSX per evitare duplicazione fra Azienda e Privato.
  const ContactsToggleButton = (
    <button
      type="button"
      onClick={() => setContactsExpanded((v) => !v)}
      className="text-xs font-medium text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
      data-testid={tid("contacts-toggle")}
    >
      {contactsExpanded ? "− Dettagli" : "+ Dettagli"}
    </button>
  );

  const PhoneEmailFields = (phonePlaceholder, emailPlaceholder) => (
    <div className="grid grid-cols-2 gap-2" data-testid={tid("contacts-fields")}>
      <div>
        <Label>Telefono</Label>
        <Input
          value={value.owner_phone || ""}
          onChange={(e) => onChange({ owner_phone: e.target.value })}
          placeholder={phonePlaceholder}
          data-testid={tid("owner-phone")}
        />
      </div>
      <div>
        <Label>Email</Label>
        <Input
          type="email"
          value={value.owner_email || ""}
          onChange={(e) => onChange({ owner_email: e.target.value })}
          placeholder={emailPlaceholder}
          data-testid={tid("owner-email")}
        />
      </div>
    </div>
  );

  const AziendaBlock = (
    <div className="space-y-3 border-t pt-3" data-testid={tid("azienda-block")}>
      <div>
        <Label>P.IVA *</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input
            value={value.owner_vat_number || ""}
            onChange={(e) => onChange({ owner_vat_number: e.target.value })}
            placeholder="12345678901"
            inputMode="numeric"
            data-testid={tid("vat")}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => runVatLookup()}
            disabled={vatLoading}
            data-testid={tid("vat-lookup")}
          >
            {vatLoading ? "Ricerca…" : "Verifica P.IVA"}
          </Button>
        </div>
        {vatError && (
          <div className="text-xs text-red-600 mt-1" data-testid={tid("vat-lookup-error")}>
            {vatError}
          </div>
        )}
        {vatDone && !vatError && (
          <div className="text-xs text-green-700 mt-1" data-testid={tid("vat-lookup-success")}>
            Dati aziendali autocompilati da VIES.
          </div>
        )}
        {!aziendaRevealed && !vatLoading && !vatError && !vatDone && (
          <p className="text-[11px] text-muted-foreground mt-2">
            Alla digitazione di 11 cifre interroghiamo VIES (API UE) e, se
            trovata, compiliamo automaticamente Ragione sociale e Indirizzo.{" "}
            <button
              type="button"
              onClick={() => setAziendaRevealed(true)}
              className="underline hover:text-purple-700"
              data-testid={tid("vat-manual-link")}
            >
              Inserisci manualmente
            </button>
          </p>
        )}
      </div>

      {aziendaRevealed && (
        <>
          <div>
            <Label>Ragione sociale *</Label>
            <Input
              value={value.owner_company_name || ""}
              onChange={(e) => onChange({ owner_company_name: e.target.value })}
              placeholder="Immobiliare Rossi SRL"
              data-testid={tid("company-name")}
            />
          </div>
          <div>
            <Label>Indirizzo sede legale</Label>
            <Input
              value={value.owner_company_address || ""}
              onChange={(e) => onChange({ owner_company_address: e.target.value })}
              placeholder="Via Milano 12, 20100 Milano (MI)"
              data-testid={tid("company-address")}
            />
          </div>
          <div className="pt-1">{ContactsToggleButton}</div>
          {contactsExpanded && PhoneEmailFields("+39 02 1234567", "info@azienda.it")}
        </>
      )}
    </div>
  );

  const PrivatoBlock = (
    <>
      <div className="grid grid-cols-2 gap-2 border-t pt-3" data-testid={tid("privato-block")}>
        <div>
          <Label>Nome *</Label>
          <Input
            value={value.owner_first_name || ""}
            onChange={(e) => onChange({ owner_first_name: e.target.value })}
            data-testid={tid("owner-fn")}
          />
        </div>
        <div>
          <Label>Cognome *</Label>
          <Input
            value={value.owner_last_name || ""}
            onChange={(e) => onChange({ owner_last_name: e.target.value })}
            data-testid={tid("owner-ln")}
          />
        </div>
      </div>
      <div className="pt-1">{ContactsToggleButton}</div>
      {contactsExpanded && PhoneEmailFields("+39 333 1234567", "mario.rossi@email.it")}
    </>
  );

  const OwnerHint = (
    <div
      className="border-t pt-3 text-sm text-muted-foreground italic"
      data-testid={tid("owner-type-required")}
    >
      Seleziona prima "Privato" o "P.IVA" per inserire i dati del proprietario.
    </div>
  );

  const ConditionalOwnerBlock = value.owner_type === "azienda"
    ? AziendaBlock
    : value.owner_type === "privato"
      ? PrivatoBlock
      : OwnerHint;

  const adminWithoutPro = user?.role === "admin" && !clientsProId;
  const OwnerModeSelector = (
    <div data-testid={tid("owner-mode")}>
      <Label>Proprietario *</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        {[
          { v: "existing", label: "Cliente esistente" },
          { v: "new", label: "Nuovo cliente" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => switchOwnerMode(opt.v)}
            className={
              "border-2 rounded-md py-2 text-sm font-medium transition-all " +
              (ownerMode === opt.v
                ? "border-purple-500 bg-purple-50 text-purple-800"
                : "border-slate-200 bg-white text-slate-700 hover:border-purple-300")
            }
            data-testid={tid(`owner-mode-${opt.v}`)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  const ExistingClientBlock = adminWithoutPro ? (
    <div className="border-t pt-3 text-sm text-muted-foreground italic" data-testid={tid("client-no-pro")}>
      Seleziona prima un professionista.
    </div>
  ) : (
    <div className="border-t pt-3">
      {pickedClient
        ? <OwnerClientSummary client={pickedClient} onChange={unpickClient} tid={tid} />
        : <OwnerClientSearch proId={user?.role === "admin" ? clientsProId : null} onPick={pickClient} onCreateNew={() => switchOwnerMode("new")} tid={tid} />}
    </div>
  );

  const OwnerSection = clientPicker ? (
    <>
      {OwnerModeSelector}
      {ownerMode === "existing" && ExistingClientBlock}
      {ownerMode === "new" && (
        <>
          {OwnerTypeSelector}
          {ConditionalOwnerBlock}
        </>
      )}
      {!ownerMode && (
        <div className="border-t pt-3 text-sm text-muted-foreground italic" data-testid={tid("owner-mode-required")}>
          Scegli se il proprietario è un cliente già in rubrica o un nuovo cliente.
        </div>
      )}
    </>
  ) : (
    <>
      {OwnerTypeSelector}
      {ConditionalOwnerBlock}
    </>
  );

  // =============== LAYOUT ==================
  if (wizard) {
    return (
      <div className="space-y-3" data-testid={tid("form")}>
        {step === 1 && PropertyFieldsBlock}
        {step === 2 && OwnerSection}
        <div className="flex gap-2 pt-2">
          {step === 1 && onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} data-testid={tid("cancel")}>
              {cancelLabel}
            </Button>
          )}
          {step === 2 && (
            <Button type="button" variant="ghost" onClick={goBack} data-testid={tid("back")}>
              ← Indietro
            </Button>
          )}
          {step === 1 ? (
            <Button
              type="button"
              className="flex-1 ht-gradient border-0"
              onClick={goNext}
              data-testid={tid("next")}
            >
              Avanti · Proprietario →
            </Button>
          ) : (
            <Button
              type="button"
              className="flex-1 ht-gradient border-0"
              onClick={handleSubmit}
              disabled={submitting || submitBlocked}
              data-testid={tid("submit")}
            >
              {submitting ? "Creazione…" : submitLabel}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Single-step layout (marketplace, edit): tutto in sequenza.
  return (
    <div className="space-y-3" data-testid={tid("form")}>
      {!hideOwnerFields && !clientPicker && OwnerTypeSelector}
      {PropertyFieldsBlock}
      {!hideOwnerFields && (clientPicker ? OwnerSection : ConditionalOwnerBlock)}
      <div className="flex gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} data-testid={tid("cancel")}>
            {cancelLabel}
          </Button>
        )}
        <Button
          type="button"
          className="flex-1 ht-gradient border-0"
          onClick={handleSubmit}
          disabled={submitting || submitBlocked}
          data-testid={tid("submit")}
        >
          {submitting ? "Creazione…" : submitLabel}
        </Button>
      </div>
    </div>
  );
}

export function emptyNewPropertyForm() {
  return {
    owner_type: "privato",
    address: "",
    city: "",
    province: "",
    zip_code: "",
    notes: "",
    owner_first_name: "",
    owner_last_name: "",
    owner_phone: "",
    owner_email: "",
    owner_vat_number: "",
    owner_company_name: "",
    owner_company_fiscal_code: "",
    owner_company_address: "",
  };
}
